import { MathUtils, Group, Clock, MirroredRepeatWrapping, ClampToEdgeWrapping, PlaneGeometry, MeshBasicMaterial, Mesh, CanvasTexture } from 'three';
import BenMarcCanvasTexture from "./BenMarcCanvasTexture";
import BenMarcLogo from "./BenMarcLogo";
import BenMarcDigitsWall from "./BenMarcDigitsWall";
import BenMarcHeroBackground from "./BenMarcHeroBackground";

export default class BenMarcHeroWorld {
    constructor(experience) {
        this.experience = experience;
        this.init();
    }

    init() {
        this.vFOV = MathUtils.degToRad(this.experience.camera.fov);
        this.backgroundHeight = this.experience.sizes.emHeight
        this.backgroundWidth = this.experience.sizes.emWidth;

        this.pixelSize = this.experience.sizes.pixelSize;

        this.world = new Group();

        this.createBackground();

        this.createAnimatedBackground();

        this.createDrawingPane();

        this.createDrawingPaneMask();

        this.createDrawingLogo();
        this.createDrawingLogoMask();

        this.clock = new Clock();
        this.previousTime = 0;
        this.animate();
    }

    createAnimatedBackground() {
        this.animatedWall = new BenMarcDigitsWall(this.experience);
    }

    createBackground() {
        this.background = new BenMarcHeroBackground(this.experience);

        this.world.add(this.background.mesh);
    }

    createDrawingLogo() {
        this.logo = {
            maxWidth: 667,
            maxHeight: 291,
            aspectRatio: 667 / 291,
        }

        const paneToLogoRatio = this.pane.maxWidth / this.logo.maxWidth;

        this.logo.widthPx = Math.min(this.pane.paneWidthPx / paneToLogoRatio, this.logo.maxWidth);
        this.logo.heightPx = this.logo.widthPx / this.logo.aspectRatio;

        this.logo.width = this.logo.widthPx * this.pixelSize;
        this.logo.height = this.logo.heightPx * this.pixelSize;

        const logoGeometry = new PlaneGeometry(this.logo.width, this.logo.height);
        const logoMaterial = new MeshBasicMaterial({
            map: this.experience.textures.logo.map,
            transparent: true,
        });

        this.logo.mesh = new Mesh(logoGeometry, logoMaterial);

        this.logo.mesh.position.x = this.pane.mesh.position.x;
        this.logo.mesh.position.y = this.pane.mesh.position.y;
        this.logo.mesh.position.z = 0;

        this.world.add(this.logo.mesh);
    }


    createDrawingLogoMask() {
        this.logoMask = {
            width: this.logo.maxWidth,
            height: this.logo.maxHeight,
        }

        this.logoMask.canvas = new BenMarcLogo(this.logoMask.width, this.logoMask.height, 1);

        this.logoMask.maskTexture = new CanvasTexture(this.logoMask.canvas.canvas);
        this.logoMask.maskTexture.wrapS = ClampToEdgeWrapping;
        this.logoMask.maskTexture.wrapT = ClampToEdgeWrapping;
        this.logo.mesh.material.alphaMap = this.logoMask.maskTexture;
    }

    createDrawingPane() {
        this.pane = {
            maxWidth: 762,
            maxHeight: 458,
            aspectRatio: 762 / 458,
        }

        // pane size is 55% of the container width but not more than maxWidth
        this.pane.paneWidthPx = Math.min(this.experience.sizes.width * 0.55, this.pane.maxWidth);
        this.pane.paneHeightPx = this.pane.paneWidthPx / this.pane.aspectRatio;

        this.pane.width = this.pane.paneWidthPx * this.pixelSize;
        this.pane.height = this.pane.paneHeightPx * this.pixelSize;

        const paneGeometry = new PlaneGeometry(this.pane.width, this.pane.height);
        this.pane.paneMaterial = new MeshBasicMaterial({
            map: this.experience.textures.drawing.map,
            // color: 0x000000,
            transparent: true,
        });

        this.pane.mesh = new Mesh(paneGeometry, this.pane.paneMaterial);
        // left of the pane is center of the screen minus half of the pane width
        if(window.innerWidth < 768) {
            const posY = (this.experience.sizes.height / 2 - (this.experience.sizes.height - this.experience.DOM.container.height) / 2) * this.pixelSize;
            this.pane.mesh.position.x = 0;
            this.pane.mesh.position.y = posY;

        } else {
            if(this.pane.width * 2 > this.backgroundWidth) {
                // put pane to the right of the screen
                this.pane.mesh.position.x = this.backgroundWidth / 2 - this.pane.width / 2;
            } else {
                this.pane.mesh.position.x = this.pane.width / 2;
            }

            this.pane.mesh.position.y = 0;
        }

        this.pane.mesh.position.z = 0;

        this.world.add(this.pane.mesh);
    }

    createDrawingPaneMask() {
        this.mask = {
            width: this.pane.width,
            height: this.pane.height,
        }
        this.mask.widthPx = this.mask.width / this.pixelSize;
        this.mask.heightPx = this.mask.height / this.pixelSize;

        this.mask.canvas = new BenMarcCanvasTexture(this.mask.widthPx, this.mask.heightPx);


        this.mask.maskTexture = new CanvasTexture(this.mask.canvas.canvas);
        this.mask.maskTexture.wrapS = ClampToEdgeWrapping;
        this.mask.maskTexture.wrapT = ClampToEdgeWrapping;

        this.pane.paneMaterial.alphaMap = this.mask.maskTexture;
        // this.pane.paneMaterial.map = this.mask.maskTexture;
    }

    updateDrawing() {
        const elapsedTime = this.clock.getElapsedTime();
        const delta = elapsedTime - this.previousTime;
        this.previousTime = elapsedTime;

        this.mask.maskTexture.needsUpdate = this.mask.canvas.animate(delta);
        this.logoMask.maskTexture.needsUpdate = true;
    }

    animate() {
        window.requestAnimationFrame(() => {
            this.animate();
        })

        this.updateDrawing();
        this.animatedWall.update();
    }

    resize() {
        this.backgroundHeight = this.experience.sizes.emHeight;
        this.backgroundWidth = this.experience.sizes.emWidth;

        this.pixelSize = this.experience.sizes.pixelSize;

        // const widthPrev = this.experience.sizes.widthPrev;
        const width = this.experience.sizes.width;

        // const heightPrev = this.experience.sizes.heightPrev;
        // const height = this.experience.sizes.height;

        // const scaleWidth = width / widthPrev;
        // const scaleHeight = height / heightPrev;

        // resize the pane
        const paneWidthPx = Math.min(width * 0.55, this.pane.maxWidth);
        const paneHeightPx = paneWidthPx / this.pane.aspectRatio;
        const paneWidthPrev = this.pane.width;
        const paneHeightPrev = this.pane.height;

        this.pane.width = paneWidthPx * this.pixelSize;
        this.pane.height = paneHeightPx * this.pixelSize;

        const scalePaneWidth = this.pane.width / paneWidthPrev;
        const scalePaneHeight = this.pane.height / paneHeightPrev;

        this.pane.mesh.scale.x *= scalePaneWidth;
        this.pane.mesh.scale.y *= scalePaneHeight;
        if(window.innerWidth < 768) {
            const posY = (this.experience.sizes.height / 2 - (this.experience.sizes.height - this.experience.DOM.container.height) / 2) * this.pixelSize;
            this.pane.mesh.position.x = 0;
            this.pane.mesh.position.y = posY;
        } else {
            if(this.pane.width * 2 > this.backgroundWidth) {
                this.pane.mesh.position.x = this.backgroundWidth / 2 - this.pane.width / 2;
            } else {
                this.pane.mesh.position.x = this.pane.width / 2;
            }

            this.pane.mesh.position.y = 0;
        }

        // resize the logo
        const logoWidthPx = Math.min(paneWidthPx / (this.pane.maxWidth / this.logo.maxWidth), this.logo.maxWidth);
        const logoHeightPx = logoWidthPx / this.logo.aspectRatio;

        const logoWidthPrev = this.logo.width;
        const logoHeightPrev = this.logo.height;

        this.logo.width = logoWidthPx * this.pixelSize;
        this.logo.height = logoHeightPx * this.pixelSize;

        const scaleLogoWidth = this.logo.width / logoWidthPrev;
        const scaleLogoHeight = this.logo.height / logoHeightPrev;

        this.logo.mesh.scale.x *= scaleLogoWidth;
        this.logo.mesh.scale.y *= scaleLogoHeight;

        this.logo.mesh.position.x = this.pane.mesh.position.x;
        this.logo.mesh.position.y = this.pane.mesh.position.y;

        // this.animatedBackground.scale.set(backgroundScale, backgroundScale, 1);
        this.animatedWall.resize();
        this.background.resize();
    }
}
